<template>
  <v-layout
    align-center
    justify-center
    role="region"
    aria-label="Rapid registration section"
  >
    <v-flex
      xs11
      sm11
      md11
      md4
      lg4
    >
      <v-card color="secondary">
        <v-app-bar
          flat
        >
          <v-toolbar-title class="text-body-2">
            {{
              $t("components.rapidSignUp.title")
            }}
          </v-toolbar-title>
        </v-app-bar>

        <v-container
          fluid
          grid-list-md
        >
          <!-- Busy indicator -->
          <v-progress-linear
            v-show="isLoading"
            color="accent"
            indeterminate
            aria-label="Form processing"
            aria-live="polite"
          />

          <!-- Validation errors -->
          <div
            v-for="(errorsGroup, idx) in filteredErrors"
            :key="`err-grp-${idx}`"
            aria-live="assertive"
            role="status"
          >
            <v-alert
              v-for="error in errorsGroup"
              :key="error.id"
              :type="error.type"
              :icon="error.icon"
              role="alert"
            >
              {{ error.msg }}
            </v-alert>
          </div>

          <!--  Form  -->
          <v-form
            ref="form"
            role="form"
            aria-label="Rapid sign-up form"
            @submit.prevent="validate"
          >
            <!-- Email -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.email"
                  v-validate="'required|email|min:6|max:255'"
                  name="email"
                  type="email"
                  prepend-icon="fal fa-envelope"
                  color="accent"
                  data-vv-name="email"
                  data-vv-as="email"
                  :error-messages="errors.collect('email')"
                  :label="$t('components.rapidSignUp.form.email') + ' *'"
                  aria-required="true"
                  :aria-invalid="errors.has('email')"
                />
              </v-flex>
            </v-layout>

            <!-- T&C / Policy -->
            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="form.acceptedTermsAndPolicy"
                  color="accent"
                  :aria-checked="form.acceptedTermsAndPolicy"
                  aria-label="Accept Terms &amp; Conditions and Policy"
                >
                  <template #label>
                    <div>
                      {{ $t('components.rapidSignUp.form.acceptTermsAndPolicy') }}
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <a
                            target="_blank"
                            :href="toTCLink"
                            class="register-important-link"
                            aria-label="Read Terms and Conditions (opens in new tab)"
                            @click.stop
                            v-on="on"
                          >
                            Terms & Conditions
                          </a>
                        </template>
                        {{ $t('components.rapidSignUp.form.acceptTermsDialog') }}
                      </v-tooltip>
                      and
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <a
                            target="_blank"
                            :href="toPolicyLink"
                            class="register-important-link"
                            aria-label="Read Privacy Policy (opens in new tab)"
                            @click.stop
                            v-on="on"
                          >
                            Policy
                          </a>
                        </template>
                        {{ $t('components.rapidSignUp.form.acceptPolicyDialog') }}
                      </v-tooltip>
                    </div>
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <!-- Marketing switch -->
            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="form.acceptMarketingNews"
                  color="accent"
                  flat
                  :aria-checked="form.acceptMarketingNews"
                  :aria-label="$t('components.rapidSignUp.form.acceptMarketingNews')"
                >
                  <template #label>
                    <div>
                      {{ $t('components.rapidSignUp.form.acceptMarketingNews') }}
                    </div>
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <v-card-actions>
              <v-bottom-sheet
                v-model="isSheetOpen"
                :aria-labelledby="'download-sheet-title'"
                :retain-focus="true"
                role="dialog"
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-if="canShowDownloadBtn"
                    color="accent"
                    dark
                    aria-label="Open download list"
                    :aria-controls="'download-sheet'"
                    v-bind="attrs"
                    v-on="on"
                    @keydown.space.prevent="attrs.click()"
                  >
                    {{
                      $t(
                        "components.rapidSignUp.buttons.download"
                      )
                    }}
                  </v-btn>
                </template>

                <v-list
                  id="download-sheet"
                  role="list"
                >
                  <v-subheader id="download-sheet-title">
                    {{
                      $t(
                        "components.rapidSignUp.download.header"
                      )
                    }}
                  </v-subheader>

                  <v-list-item
                    v-for="game in downloadGames"
                    :key="game.id"
                    role="listitem"
                    tabindex="0"
                    link
                    :aria-label="`${$t(game.title)}${game.isNew ? ' (New)' : ''}${game.isRecommend ? ' (Recommended)' : ''}`"
                    @keydown.enter="goTo(game.link)"
                    @keydown.space.prevent="goTo(game.link)"
                    @click="goTo(game.link)"
                  >
                    <div
                      v-if="game.useImageAsBackground"
                      class="v-list-item-background-image"
                      :style="`background-image: url('${game.img}')`"
                      :aria-label="`${$t(game.title)} illustration`"
                    >
                      <div class="v-list-item-download-labels">
                        <span
                          v-if="game.isNew"
                          class="downloadLabel downloadNew"
                        >New</span>
                        <span
                          v-if="game.isRecommend"
                          class="downloadLabel downloadRecommend"
                        >Recommend</span>
                        <span
                          v-if="game.isRecommend"
                          class="downloadLabel downloadComingSoon"
                        >Coming Soon</span>
                      </div>
                    </div>

                    <template v-else>
                      <v-list-item-avatar size="25">
                        <v-icon>{{ game.icon }}</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-title
                        class="text-no-wrap"
                      >
                        {{ $t(game.title) }}
                        <div class="v-list-item-download-labels">
                          <span
                            v-if="game.isNew"
                            class="downloadLabel downloadNew"
                          >New</span>
                          <span
                            v-if="game.isRecommend"
                            class="downloadLabel downloadRecommend"
                          >Recommend</span>
                          <span
                            v-if="game.isRecommend"
                            class="downloadLabel downloadComingSoon"
                          >Coming Soon</span>
                        </div>
                      </v-list-item-title>
                    </template>
                  </v-list-item>
                </v-list>
              </v-bottom-sheet>

              <v-spacer />

              <!-- Submit -->
              <p
                id="registerHelp"
                class="sr-only"
              >
                Submit your email and consent to create your account.
              </p>
              <v-btn
                color="accent"
                type="submit"
                :disabled="!(form.acceptedTermsAndPolicy)"
                aria-label="SignUp"
                aria-describedby="registerHelp"
              >
                {{
                  $t("components.rapidSignUp.buttons.register")
                }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-container>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import { filterByKeys } from "@/utils/object.utils";
import downloadGames from "@/constants/download.games";
import blacklist from "@/constants/blacklist";
import links from "@/constants/links";
import Validator from "@/modules/validator.module";

const ValidatorModule = new Validator();

  export default {
    name: "RapidRegisterForm",
    components: {
        // eslint-disable-next-line vue/no-unused-components
        ValidationProvider
    },
    inject: ["$validator"],
    emits: {'onFinishedRegistration': null, 'onStartedRegistration': null},
    data() {
        const defaultForm = Object.freeze({
            email: "",
            acceptedTermsAndPolicy: false,
            acceptMarketingNews: false,
        });

        return {
            form: Object.assign({}, defaultForm),
            downloadGames: downloadGames.list,
            isLoading: false,
            isSheetOpen: false,
            isCompletedRegistration: false,
            defaultForm
        }
    },
    computed: {
        filteredErrors() {
            return filterByKeys(
                this.$validator.errors.collect(),
                blacklist.forms.rapidRegister
            );
        },
        toForgotPassLink() {
            return links.routes.resetLink;
        },
        toSignInLink() {
            return links.routes.login;
        },
        toTCLink() {
            return links.routes.tcConditions;
        },
        toPolicyLink() {
            return links.routes.tcPolicy;
        },
        canShowDownloadBtn() {
            return this.isCompletedRegistration && this.downloadGames.length && this.$vuetify.breakpoint.mdAndUp
        }
    },
    watch: {
        'form.acceptedTermsAndPolicy'(newValue) {
            if (newValue) {
                ValidatorModule.clear();

                return;
            }

            ValidatorModule.add("acceptedTermsAndPolicy", {
                type: 0,
                msg: 'Terms & Conditions, Policy is required to be accepted to proceed'
            });
        },
        isSheetOpen(value) {
            if (this.isCompletedRegistration && !value) {
                this.$emit('onFinishedRegistration', true);

                this.resetForm();
            }
        }
    },
    mounted() {
        this.$emit('onStartedRegistration', false);
        this.$emit('onFinishedRegistration', false);
    },
    methods: {
        async validate() {
            if (await this.$validator.validateAll()) {
                if (!this.form.acceptedTermsAndPolicy) {
                    return;
                }

                this.$emit('onStartedRegistration', true);

                this.isCompletedRegistration = false;
                this.isSheetOpen = false;
                this.isLoading = true;
                this.$api.rapidRegister(
                        this.form.email,
                    )
                .then(() => {
                    this.isLoading = false;
                    this.isSheetOpen = true;
                    this.isCompletedRegistration = true;
                })
                .catch(() => {
                    this.isLoading = false;
                });
            }
        },
        resetForm () {
            this.form = Object.assign({}, this.defaultForm)

            this.$refs.form.reset()
        },
        goTo(link) {
            // track event
            // this.$ga.event("rapidRegister", "download-game", link);
            // go to link
            window.open(link);
        },
    }
  }
</script>


<style scoped>
/* Component-specific styles */
</style>
